/**
 * Date derivation for the Morgenlage briefing (#3136).
 *
 * The page ships 17 date strings. They sit on only FIVE bases plus one ISO week,
 * so this module derives those bases once from an injected `now` and the component
 * formats them — rather than 17 ad-hoc fixes that can drift apart from each other.
 *
 * Three rules this module exists to enforce:
 *
 * 1. LOCAL TIME, NEVER UTC. `frontend/src/utils/today.ts` is deliberately UTC; reusing
 *    that basis here would name the wrong weekday for a German farmer reading the page
 *    between 00:00 and 02:00 CEST. Every helper below normalises to the LOCAL civil day.
 * 2. ONE basis per meaning. `datenstand` feeds the header, the WhatsApp caption, the
 *    heat-day chip and the footer; `engpass` feeds three separate figures. They cannot
 *    disagree because there is only one of each.
 * 3. LOCALE IS HARDCODED 'de-DE'. Morgenlage has no `useTranslation` at all — claiming
 *    i18n surface here would collide with #3132, which owns language resolution.
 */

const LOCALE = 'de-DE';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/* One formatter instance each — constructing an Intl.DateTimeFormat is the expensive
   part, formatting with it is not, and these are pure/stateless so module scope is safe. */
const longDateFormat = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });
const weekdayLongFormat = new Intl.DateTimeFormat(LOCALE, { weekday: 'long' });
const weekdayShortFormat = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' });
const ddMmFormat = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' });
const ddMmYyyyFormat = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * `base` shifted by `days`, normalised to LOCAL midnight.
 *
 * `new Date(y, m, d + n)` is deliberate: it rolls months and years over for us and it
 * lands on local midnight of the target civil day, so it stays correct across a DST
 * transition (unlike adding 86_400_000 ms).
 */
export function addDays(base: Date, days: number): Date {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
}

/** "Mittwoch, 12. August" */
export function formatLongDate(date: Date): string {
  return longDateFormat.format(date);
}

/** "Donnerstag" — the prose weekday. */
export function formatWeekdayLong(date: Date): string {
  return weekdayLongFormat.format(date);
}

/** "Di" — de-DE short weekday, no trailing dot. */
export function formatWeekdayShort(date: Date): string {
  return weekdayShortFormat.format(date);
}

/** "11.08." — trailing dot is part of the German format, not a typo. */
export function formatDdMm(date: Date): string {
  return ddMmFormat.format(date);
}

/** "11.08.2026" */
export function formatDdMmYyyy(date: Date): string {
  return ddMmYyyyFormat.format(date);
}

/** "Di, 11.08." */
export function formatShortDate(date: Date): string {
  return `${formatWeekdayShort(date)}, ${formatDdMm(date)}`;
}

/** "Di, 11.08.2026" — the share caption and the footer carry the year. */
export function formatShortDateWithYear(date: Date): string {
  return `${formatWeekdayShort(date)}, ${formatDdMmYyyy(date)}`;
}

/**
 * Midnight of the ISO week's Monday. Idempotent on a Monday; a Sunday maps BACK six
 * days, never forward — an ISO week ends on Sunday, it does not start on one.
 */
export function isoWeekMonday(date: Date): Date {
  const dayOfWeek = date.getDay(); // 0 = Sonntag … 6 = Samstag
  return addDays(date, -((dayOfWeek + 6) % 7)); // Mo → 0 … So → 6
}

/**
 * ISO week number (1…53) plus the ISO WEEK-YEAR, which differs from the calendar year
 * at the rollover: 2027-01-01 is a Friday, so it is still KW 53/2026, while 2025-12-29
 * is a Monday whose Thursday is 2026-01-01, so it is already KW 1/2026.
 *
 * Derived from the week's own Thursday (the ISO ownership rule) and then counted off
 * that week-year's KW 1, which is by definition the week containing Jan 4.
 *
 * Hand-ported from `frontend/src/pages/apps/ernteprognose/outputSeries.ts:151-190`
 * (`isoWeekStart` / `isoWeekParts`) — the design-system cannot import from `frontend/`,
 * the dependency runs frontend → DS only. That reference version works on UTC ISO
 * strings; this one works on local `Date`s, so the week-span subtraction goes through
 * `Date.UTC` on the civil Y/M/D to stay exact across a DST boundary.
 */
export function isoWeekParts(date: Date): { week: number; weekYear: number } {
  const monday = isoWeekMonday(date);
  const thursday = addDays(monday, 3);
  const weekYear = thursday.getFullYear();
  const firstWeekMonday = isoWeekMonday(new Date(weekYear, 0, 4));
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const week = Math.round((utc(monday) - utc(firstWeekMonday)) / (7 * MS_PER_DAY)) + 1;
  return { week, weekYear };
}

/** The last Mon–Fri strictly before `date`. */
export function previousBusinessDay(date: Date): Date {
  let candidate = addDays(date, -1);
  while (candidate.getDay() === 0 || candidate.getDay() === 6) {
    candidate = addDays(candidate, -1);
  }
  return candidate;
}

/** How many days the Ernte-Ausblick card projects, today included. */
export const FORECAST_LENGTH = 4;

export interface MorgenlageDates {
  /** Local civil today — the page header and the live-signal stamp. */
  today: Date;
  /**
   * The last day the twin has complete numbers for: today − 1, ALWAYS.
   *
   * The issue words this as the "previous completed business day", which on a Monday
   * is Friday — while the Teams and Sorten cards say "gestern", which on a Monday is
   * Sunday. Left naive the page contradicts itself once a week.
   *
   * Resolved in favour of the plain calendar day: a berry farm harvests seven days a
   * week and wiberry syncs the Sunday pick like any other, so there is no "business
   * day" in harvest data. "gestern" is now true by construction on every day of the
   * week — the two cannot disagree because there is no branch that lets them.
   */
  datenstand: Date;
  /**
   * The last business day BEFORE `datenstand` — the Kontoauszüge stamp.
   *
   * This is the one place "business day" is genuinely the right concept, because banks
   * really do not post at the weekend. On the mock's authored Wednesday it lands on
   * Mon 10.08, deliberately staler than the twin, exactly as the literal it replaces.
   */
  bankStand: Date;
  /** Monday of the UPCOMING ISO week — the basis for both `engpass` and `nextWeek`. */
  nextWeekMonday: Date;
  /**
   * The liquidity trough: Tuesday of the upcoming ISO week, so it always falls inside
   * the very week the Preis-Empfehlung card names. A fixed today + 6 offset would drift
   * out of that week every Monday.
   */
  engpass: Date;
  /**
   * ISO week of `nextWeekMonday` — the Preis-Empfehlung card's "KW nn".
   * This is the UPCOMING week, not the current one: reading it as "current" would
   * silently change what the card means.
   */
  nextWeek: { week: number; weekYear: number };
  /** `FORECAST_LENGTH` consecutive days starting today — the Ernte-Ausblick bars. */
  forecast: Date[];
}

/** Every date basis the page needs, derived once from an injected `now`. */
export function morgenlageDates(now: Date): MorgenlageDates {
  const today = addDays(now, 0); // normalise to local midnight
  const datenstand = addDays(today, -1);
  const nextWeekMonday = addDays(isoWeekMonday(today), 7);

  return {
    today,
    datenstand,
    bankStand: previousBusinessDay(datenstand),
    nextWeekMonday,
    engpass: addDays(nextWeekMonday, 1),
    nextWeek: isoWeekParts(nextWeekMonday),
    forecast: Array.from({ length: FORECAST_LENGTH }, (_, i) => addDays(today, i)),
  };
}
